import { GRID, worldMeta, indexNav, key, POWER_TIP, GEM_SPRITE, GEM_BONUS } from "./navShared";

// Rock crumbling into shards + a dust puff (plays once when an active drill bores through)
const SHARDS = [
  { clip: "polygon(0 0, 50% 0, 50% 50%, 0 50%)", dx: -55, dy: -55, r: -40 }, { clip: "polygon(50% 0, 100% 0, 100% 50%, 50% 50%)", dx: 55, dy: -50, r: 35 },
  { clip: "polygon(0 50%, 50% 50%, 50% 100%, 0 100%)", dx: -50, dy: 55, r: 30 }, { clip: "polygon(50% 50%, 100% 50%, 100% 100%, 50% 100%)", dx: 55, dy: 55, r: -30 },
  { clip: "polygon(30% 30%, 70% 30%, 70% 70%, 30% 70%)", dx: 0, dy: -70, r: 60 },
];
const RockBreak = ({ src, sizeClass }) => (
  <div className="absolute inset-0 pointer-events-none" data-testid="nav-rock-break">
    <div className="nav-dust absolute inset-[10%] rounded-full" />
    <div className={`absolute inset-0 flex items-center justify-center`}>
      <div className={`relative ${sizeClass}`}>
        {SHARDS.map((sh, i) => (
          <img key={i} src={src} alt="" className="nav-shard absolute inset-0 w-full h-full" draggable={false}
            style={{ clipPath: sh.clip, "--dx": `${sh.dx}%`, "--dy": `${sh.dy}%`, "--r": `${sh.r}deg`, animationDelay: `${i * 20}ms` }} />
        ))}
      </div>
    </div>
  </div>
);

// Static 10x10 board themed by the grid's world. `drill` = { x, y, deg, hidden, active } overrides the start position (used during playback).
// `onCellClick(x, y)` turns the board into an editor surface; `flash` marks a cell with an effect; `collected` hides picked-up goals / fuel cells / power-ups;
// `broken` hides rocks the drill has bored through and `breaking` plays the crumble on one cell.
// `gems` = revealed hidden gems (shown on the board); `gem` = the one popping right now; `hiddenGems` = designer view (editor only).
export const NavGrid = ({ nav, drill, onCellClick, flash, collected, broken, breaking, gem, gems, hiddenGems, className = "", showStart = true, cellTestId = "nav-cell", children }) => {
  const world = worldMeta(nav);
  const cells = indexNav(nav);
  const cols = nav?.cols || GRID;
  const rows = nav?.rows || GRID;
  const d = drill || (nav?.start ? { x: nav.start.x, y: nav.start.y, deg: { N: 0, E: 90, S: 180, W: 270 }[nav.start.dir] || 0 } : null);
  const pct = 100 / cols;
  const taken = new Set(collected || []);
  const brokenSet = new Set(broken || []);
  const revealed = new Set((gems || []).map((g) => key(g.x ?? g[0], g.y ?? g[1])));
  const secret = new Set((hiddenGems || []).map((g) => key(g.x, g.y)));

  return (
    <div
      className={`relative w-full aspect-square rounded-xl overflow-hidden border-4 ${world.border} shadow-inner select-none ${className}`}
      style={{ backgroundColor: world.bg }}
      data-testid="nav-grid"
      data-world={world.id}
    >
      {/* grid lines */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(${world.line} 1px, transparent 1px), linear-gradient(90deg, ${world.line} 1px, transparent 1px)`,
        backgroundSize: `${pct}% ${100 / rows}%`,
      }} />
      {Array.from({ length: rows }).map((_, y) => Array.from({ length: cols }).map((__, x) => {
        const item = cells[key(x, y)];
        const isStart = showStart && nav?.start && nav.start.x === x && nav.start.y === y;
        const gone = item && (item.kind === "goal" || item.kind === "fuel" || item.kind === "power") && taken.has(key(x, y));
        const rockGone = item?.kind === "obstacle" && brokenSet.has(key(x, y));
        const rockBreaking = item?.kind === "obstacle" && breaking === key(x, y);
        return (
          <div
            key={key(x, y)}
            className={`absolute flex items-center justify-center ${onCellClick ? "cursor-pointer hover:bg-white/15" : ""}`}
            style={{ left: `${x * pct}%`, top: `${y * (100 / rows)}%`, width: `${pct}%`, height: `${100 / rows}%` }}
            onClick={onCellClick ? () => onCellClick(x, y) : undefined}
            data-testid={`${cellTestId}-${x}-${y}`}
            data-item={item?.kind || (isStart ? "start" : "")}
            data-collected={gone ? "true" : undefined}
            data-broken={rockGone ? "true" : undefined}
          >
            {item?.kind === "obstacle" && !rockGone && !rockBreaking && <img src={world.sprites.obstacle} alt="" className={world.size.obstacle} draggable={false} />}
            {rockBreaking && <RockBreak src={world.sprites.obstacle} sizeClass={world.size.obstacle} />}
            {revealed.has(key(x, y)) && (
              <img src={GEM_SPRITE} alt="Hidden gem found" title="Hidden gem found (+10)" className="w-[62%] h-[62%] drop-shadow-[0_0_8px_rgba(16,185,129,0.9)]" draggable={false} data-testid={`nav-gem-found-${x}-${y}`} />
            )}
            {secret.has(key(x, y)) && !revealed.has(key(x, y)) && (
              <div className="absolute inset-[14%] rounded-full border-2 border-dashed border-emerald-400/80 bg-emerald-400/10 flex items-center justify-center" title="Hidden gem (players can't see this)" data-testid={`nav-hidden-gem-${x}-${y}`}>
                <img src={GEM_SPRITE} alt="Hidden gem" className="w-[70%] h-[70%] opacity-60" draggable={false} />
              </div>
            )}
            {item?.kind === "power" && !gone && (
              <img src={world.sprites.power} alt="Activate drill power-up" title={POWER_TIP} className={`${world.size.power} nav-power ${flash === "power" ? "scale-110" : ""}`} draggable={false} data-testid={`nav-power-${x}-${y}`} />
            )}
            {item?.kind === "danger" && <img src={world.sprites.danger} alt="" className={world.size.danger} draggable={false} />}
            {item?.kind === "goal" && !gone && <img src={world.sprites.goal} alt="" className={`${world.size.goal} ${flash === "treasure" ? "animate-bounce" : ""}`} draggable={false} />}
            {item?.kind === "fuel" && !gone && <img src={world.sprites.fuel} alt="" className={world.size.fuel} draggable={false} />}
            {item?.kind === "tunnel" && (
              <div className="relative w-[86%] h-[86%]">
                <img src={world.sprites.tunnel} alt="" className="w-full h-full" draggable={false} />
                <span className="absolute inset-0 flex items-center justify-center font-heading font-black text-white text-[clamp(10px,2.4vw,22px)] drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{item.letter}</span>
              </div>
            )}
            {isStart && onCellClick && (
              <div className="absolute inset-0 rounded ring-2 ring-cyan-300/70 pointer-events-none" />
            )}
          </div>
        );
      }))}
      {/* Scanner: green radar sweep around the drill while a loop / if is running (glides with the drill) */}
      {d && !d.hidden && d.scanning && (
        <div
          className="absolute pointer-events-none nav-scanner"
          style={{
            left: `${(d.x + 0.5) * pct}%`, top: `${(d.y + 0.5) * (100 / rows)}%`, width: `${pct * 2.6}%`, aspectRatio: "1 / 1", transform: "translate(-50%, -50%)",
            transition: `left ${d.ms ?? 300}ms ease-in-out, top ${d.ms ?? 300}ms ease-in-out`,
          }}
          data-testid="nav-scanner"
        >
          <div className="nav-scanner-ring absolute inset-0 rounded-full" />
          <div className="nav-scanner-sweep absolute inset-0 rounded-full" />
          <div className="absolute inset-[38%] rounded-full border border-emerald-300/50" />
        </div>
      )}
      {d && !d.hidden && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: `${d.x * pct}%`, top: `${d.y * (100 / rows)}%`, width: `${pct}%`, height: `${100 / rows}%`,
            transition: `left ${d.ms ?? 300}ms ease-in-out, top ${d.ms ?? 300}ms ease-in-out, transform ${d.ms ?? 300}ms ease-in-out`,
            transform: `rotate(${d.deg}deg)`,
          }}
          data-testid="nav-drill"
          data-x={d.x} data-y={d.y}
          data-active={d.active ? "true" : undefined}
        >
          <img src={world.sprites.vehicle} alt={world.labels.vehicle} className={`w-full h-full ${d.active ? "nav-drill-on" : ""} ${flash === "danger" || flash === "out_of_fuel" ? "animate-pulse" : ""} ${flash === "bump" ? "brightness-125" : ""} ${flash === "out_of_fuel" ? "grayscale" : ""}`} draggable={false} />
          {d.active && (
            <>
              {/* spinning threads over the drill bit (front of the sprite) + a glow at the tip */}
              <div className="nav-drill-spin absolute left-[36%] right-[36%] top-0 h-[33%]" data-testid="nav-drill-spinning" />
              <div className="nav-drill-glow absolute left-[41%] right-[41%] -top-[4%] h-[14%] rounded-full" />
            </>
          )}
        </div>
      )}
      {flash === "power" && <div className="absolute inset-0 bg-orange-400/25 pointer-events-none" data-testid="nav-flash-power" />}
      {/* Hidden gem popping out of the drill */}
      {gem && (
        <div
          key={gem.key}
          className="absolute pointer-events-none nav-gem-pop flex flex-col items-center z-10"
          style={{ left: `${(gem.x + 0.5) * pct}%`, top: `${(gem.y + 0.5) * (100 / rows)}%`, width: `${pct * 1.6}%`, transform: "translate(-50%, -50%)" }}
          data-testid="nav-gem"
        >
          <div className="relative w-full aspect-square">
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
              <span key={deg} className="nav-gem-spark absolute left-1/2 top-1/2 w-[14%] h-[14%] rounded-full bg-emerald-200" style={{ "--ang": `${deg}deg`, animationDelay: `${i * 30}ms` }} />
            ))}
            <img src={GEM_SPRITE} alt="Hidden gem" className="relative w-full h-full drop-shadow-[0_0_10px_rgba(16,185,129,0.9)]" draggable={false} />
          </div>
          <span className="mt-0.5 rounded-full bg-emerald-600 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 whitespace-nowrap shadow">Hidden gem +{GEM_BONUS}</span>
        </div>
      )}
      {flash === "danger" && <div className="absolute inset-0 bg-red-500/30 animate-pulse pointer-events-none" data-testid="nav-flash-danger" />}
      {flash === "crack" && <div className="absolute inset-0 bg-amber-400/25 pointer-events-none" data-testid="nav-flash-crack" />}
      {flash === "fuel" && <div className="absolute inset-0 bg-emerald-400/25 pointer-events-none" data-testid="nav-flash-fuel" />}
      {flash === "out_of_fuel" && <div className="absolute inset-0 bg-zinc-900/40 pointer-events-none" data-testid="nav-flash-out-of-fuel" />}
      {children}
    </div>
  );
};
